import * as Plot from '@observablehq/plot';
import dayjs from 'dayjs';
import { max, repeat, sum } from 'lodash-es';
import { ChartTooltip } from '../../../components/Chart/components/ChartTooltip';
import { lodashChain } from '../../lodashUtils';
import { hoverRectWithTooltip } from '../TooltipMark/rectTooltip';
import { formatValue, getTextWidth, toTitleCase } from '../utils';
import {
	AXIS_TITLE_TO_TICK_LABEL_PADDING,
	COMMON_PADDING,
	TICK_LABEL_TO_AXIS_PADDING,
} from './constants';
import { TooltipContentRenderer } from './TooltipContentRenderer';
import type { ChartRenderFunction, LegendsInfo } from './types';

export const renderBarChart: ChartRenderFunction = ({
	results,
	chartConfig: _chartConfig,
	containerRef,
	chartWidth,
	chartHeight,
	theme,
	chartConfigHelper: chartConfigHelper,
}) => {
	const chartConfig = chartConfigHelper.getAsBarChartConfig();
	const { axes } = chartConfig;
	const xAxisColumn = axes.x[0];
	const yAxisColumns = axes.y;
	const {
		colors,
		xIsTime,
		showGrid,
		fontSize,
		lineHeight,
		shouldShowXAxisTitle,
		shouldShowYAxisTitle,
		xAxisTitle,
		yAxisTitle,
	} = chartConfigHelper;

	// Chart type flags
	const isHorizontalBarChart = chartConfig.barLayout === 'horizontal';
	const isStackedBarChart =
		chartConfig.barGrouping === 'stack' ||
		chartConfig.barGrouping === 'percentage-stack';

	const isPercentageStackedBarChart =
		chartConfig.barGrouping === 'percentage-stack';

	// Sort configuration
	let sortBy: 'ascending' | 'descending' | undefined = undefined;
	if (chartConfig.barSort === 'desc') {
		sortBy = 'descending';
	} else if (chartConfig.barSort === 'asc') {
		sortBy = 'ascending';
	}

	const textColor = theme.other.getColor('text/secondary/default');

	const commonAxisProps = { tickSize: 0, color: textColor };
	const hiddenAxisProps = { ...commonAxisProps, ticks: 0, fontSize: 0 };
	const visibleAxisProps = { ...commonAxisProps, fontSize: fontSize };

	const legends: LegendsInfo = {};
	let index = 0;

	let { xAxisTimeInterval } = chartConfigHelper;

	const data = results.flatMap((result) => {
		const categories = axes.category || [];
		const xValue = xIsTime
			? dayjs(result[xAxisColumn]).toDate()
			: result[xAxisColumn];

		return yAxisColumns.map((yAxisColumn) => {
			// Create data point with category if specified
			let barName = yAxisColumn;
			if (categories.length > 0) {
				barName = `${yAxisColumn} | ${categories.map((c) => result[c]).join(' | ')}`;
			}

			if (!legends[barName]) {
				let shortDisplayValue = toTitleCase(barName);
				if (yAxisColumns.length === 1 && categories.length > 0) {
					shortDisplayValue = toTitleCase(
						categories.map((c) => result[c]).join(' | ')
					);
				}
				legends[barName] = {
					color: colors[index % colors.length],
					shortDisplayValue,
				};
				index += 1;
			}

			// Simple data point
			return {
				_group: xValue,
				_barName: barName,
				_barValue: result[yAxisColumn],
			};
		});
	});

	const getColor = (d: Record<string, any>) => legends[d._barName].color;

	const xTickFormat = (tick: any) => {
		if (isHorizontalBarChart && isPercentageStackedBarChart) {
			return `${tick}%`;
		}
		const columnFormat = chartConfig.columnConfigs[xAxisColumn];
		return formatValue(tick, columnFormat, xAxisTimeInterval);
	};

	const yTickFormatBase = (tick: any, ignorePercentage?: boolean) => {
		if (
			!isHorizontalBarChart &&
			isPercentageStackedBarChart &&
			!ignorePercentage
		) {
			return `${tick}%`;
		}
		const columnFormat = chartConfig.columnConfigs[yAxisColumns[0]];
		return formatValue(tick, columnFormat);
	};

	const yTickFormat = (tick: any) => yTickFormatBase(tick, true);

	const fontFamily = theme.fontFamily || 'inherit';

	const { forcexAxisRotation } = chartConfigHelper;
	let xAxisRotation = chartConfigHelper.xAxisTickRotation;

	let groupTextHeight = lineHeight;
	let groupTextWidth =
		max(
			data.map((d) => getTextWidth(xTickFormat(d._group), fontFamily, fontSize))
		) || 0;

	let barValueTextHeight = lineHeight;
	let barValueTextWidth =
		max(
			data.map((d) =>
				getTextWidth(yTickFormat(d._barValue) + '.5', fontFamily, fontSize)
			)
		) || 0;

	if (!isHorizontalBarChart) {
		if (
			!forcexAxisRotation &&
			sum(
				data.map((d) =>
					getTextWidth(
						xTickFormat(d._group) + repeat(' ', 5),
						fontFamily,
						fontSize
					)
				)
			) >
				chartWidth - 64
		) {
			xAxisRotation = 45;
		}
	}

	// Downsample the xValues to maxTicks evenly (no lodash)
	let xTicksDate: Date[] = [];
	if (xIsTime) {
		// Observablehq bar chart doesn't support time scale (it must be a band scale)
		// so we cant pass interval's timeticks to the x axis
		// In this case, we must manually downsample the x values to the number of ticks
		// This should work for most cases, but the ticks wont be evenly spaced if the input dates are not evenly spaced
		const xValues = data.map((d) => d._group);
		if (data.length > 0) {
			let tickWidth = getTextWidth(
				xTickFormat(xValues[0]),
				fontFamily,
				fontSize
			);
			if (xAxisRotation >= 45) {
				tickWidth = tickWidth * Math.sin((xAxisRotation * Math.PI) / 180);
			}

			// Determine how many ticks can fit given tickWidth and chartWidth
			const minTickPadding = 8; // Minimum padding between ticks in px
			const maxTicks = Math.max(
				2,
				Math.floor((chartWidth - 64) / (tickWidth + minTickPadding))
			);

			if (xValues.length <= maxTicks) {
				xTicksDate = xValues;
			} else {
				const step = Math.ceil(xValues.length / maxTicks);
				xTicksDate = xValues.filter((_v, i) => i % step === 0);
			}
		}
	}

	if (xAxisRotation >= 45) {
		groupTextHeight =
			groupTextWidth * Math.sin((xAxisRotation * Math.PI) / 180) +
			lineHeight * Math.sin((xAxisRotation * Math.PI) / 180);
	}

	const xAxisTitleProperties = {
		label: shouldShowXAxisTitle ? xAxisTitle : null,
		labelOffset: shouldShowXAxisTitle
			? TICK_LABEL_TO_AXIS_PADDING +
				(isHorizontalBarChart ? groupTextWidth : groupTextHeight) +
				AXIS_TITLE_TO_TICK_LABEL_PADDING
			: undefined,
	};

	const yAxisTitleProperties = {
		label: shouldShowYAxisTitle ? yAxisTitle : null,
		labelOffset: shouldShowYAxisTitle
			? TICK_LABEL_TO_AXIS_PADDING +
				(isHorizontalBarChart ? barValueTextHeight : barValueTextWidth) +
				AXIS_TITLE_TO_TICK_LABEL_PADDING
			: undefined,
	};

	const marks: Plot.Markish[] = [];

	if (showGrid) {
		if (isHorizontalBarChart) {
			marks.push(
				Plot.gridX({
					stroke: theme.other.getColor('border/secondary/default'),
					strokeOpacity: 1.0,
				})
			);
		} else {
			marks.push(
				Plot.gridY({
					stroke: theme.other.getColor('border/secondary/default'),
					strokeOpacity: 1.0,
				})
			);
		}
	}

	// Frame will render for each facet by default, so we need custom render to only draw the bottom
	marks.push(
		Plot.frame({
			stroke: theme.other.getColor('border/secondary/default'),
			anchor: 'bottom',
			render: (index, scales, values, dimensions, context, next) => {
				if ('fi' in index && 'fy' in index && index.fy) {
					if (scales.scales.fy?.domain) {
						const arrayOfFy = Array.from(scales.scales.fy?.domain);
						const indexOfFy = arrayOfFy.indexOf(index.fy);
						if (indexOfFy !== arrayOfFy.length - 1) {
							return null;
						}
					}
				}
				if (!next) return null;

				const g = next(index, scales, values, dimensions, context);
				if (!g) return null;
				return g;
			},
		})
	);

	// Add axes based on chart orientation
	if (isHorizontalBarChart) {
		// Horizontal: X-axis shows values, Y-axis shows categories
		marks.push(
			Plot.axisX({
				...visibleAxisProps,
				tickFormat: yTickFormat,
				labelAnchor: 'center',
				labelArrow: 'none',
				tickPadding: TICK_LABEL_TO_AXIS_PADDING,
				...yAxisTitleProperties,
			}),
			Plot.axisY(
				isStackedBarChart
					? {
							...visibleAxisProps,
							tickFormat: xTickFormat,
							tickPadding: TICK_LABEL_TO_AXIS_PADDING,
							...xAxisTitleProperties,
						}
					: hiddenAxisProps
			)
		);

		// Add facet axis for grouped charts
		if (!isStackedBarChart) {
			marks.push(
				Plot.axisFy({
					...visibleAxisProps,
					tickFormat: xTickFormat,
					anchor: 'left',
					tickPadding: TICK_LABEL_TO_AXIS_PADDING,
					...xAxisTitleProperties,
				})
			);
		}
	} else {
		// Vertical: X-axis shows categories, Y-axis shows values
		marks.push(
			Plot.axisX(
				isStackedBarChart
					? {
							...visibleAxisProps,
							tickFormat: xTickFormat,
							ticks: xIsTime ? xTicksDate : undefined,
							rotate: -xAxisRotation,
							tickPadding:
								TICK_LABEL_TO_AXIS_PADDING +
								(xAxisRotation === 0 ? 0 : groupTextHeight / 2),
							...xAxisTitleProperties,
						}
					: hiddenAxisProps
			),
			Plot.axisY({
				...visibleAxisProps,
				tickFormat: yTickFormat,
				labelAnchor: 'center',
				labelArrow: 'none',
				...yAxisTitleProperties,
			})
		);

		// Add facet axis for grouped charts
		if (!isStackedBarChart) {
			marks.push(
				Plot.axisFx({
					...visibleAxisProps,
					tickPadding:
						TICK_LABEL_TO_AXIS_PADDING +
						(xAxisRotation === 0 ? 0 : groupTextHeight / 2),
					tickFormat: xTickFormat,
					ticks: xIsTime ? xTicksDate : undefined,
					anchor: 'bottom',
					rotate: -xAxisRotation,
					fontSize: fontSize,
					...xAxisTitleProperties,
				})
			);
		}
	}

	const createBarOptions = (
		isHorizontal: boolean
	): Plot.BarXOptions | Plot.BarYOptions => {
		const baseOptions: Plot.BarXOptions | Plot.BarYOptions = isHorizontal
			? {
					x: '_barValue',
					y: isStackedBarChart ? '_group' : '_barName',
					fy: isStackedBarChart ? undefined : '_group',
					fill: getColor,
					offset: isPercentageStackedBarChart ? 'normalize' : undefined,
					rx: 3,
					inset: isStackedBarChart ? 1 : undefined,
				}
			: {
					x: isStackedBarChart ? '_group' : '_barName',
					y: '_barValue',
					fx: isStackedBarChart ? undefined : '_group',
					fill: getColor,
					offset: isPercentageStackedBarChart ? 'normalize' : undefined,
					rx: 3,
					inset: isStackedBarChart ? 1 : undefined,
				};

		// Add sorting if specified
		if (sortBy && !xIsTime) {
			let sortChannel = '';
			if (isHorizontal) {
				if (isStackedBarChart) {
					sortChannel = 'y';
				} else {
					sortChannel = 'fy';
				}
			} else {
				if (isStackedBarChart) {
					sortChannel = 'x';
				} else {
					sortChannel = 'fx';
				}
			}

			let sortValue: string;
			if (isHorizontal) {
				sortValue = sortBy === 'ascending' ? 'x' : '-x';
			} else {
				sortValue = sortBy === 'ascending' ? 'y' : '-y';
			}

			baseOptions.sort = {
				[sortChannel]: { value: sortValue },
			};
		}

		return baseOptions;
	};

	// Add the bar chart to marks
	marks.push(
		isHorizontalBarChart
			? Plot.barX(data, createBarOptions(true) as Plot.BarXOptions)
			: Plot.barY(data, createBarOptions(false) as Plot.BarYOptions)
	);

	const margins = isHorizontalBarChart
		? {
				marginLeft:
					TICK_LABEL_TO_AXIS_PADDING +
					groupTextWidth +
					(shouldShowXAxisTitle ? AXIS_TITLE_TO_TICK_LABEL_PADDING : 0) +
					COMMON_PADDING,
				marginBottom:
					TICK_LABEL_TO_AXIS_PADDING +
					barValueTextHeight +
					(shouldShowYAxisTitle ? AXIS_TITLE_TO_TICK_LABEL_PADDING : 0) +
					COMMON_PADDING,
				marginTop: COMMON_PADDING * 2,
				marginRight: COMMON_PADDING * 2,
			}
		: {
				marginBottom:
					TICK_LABEL_TO_AXIS_PADDING +
					groupTextHeight +
					(shouldShowXAxisTitle ? AXIS_TITLE_TO_TICK_LABEL_PADDING : 0) +
					COMMON_PADDING,
				marginLeft:
					TICK_LABEL_TO_AXIS_PADDING +
					barValueTextWidth +
					(shouldShowYAxisTitle ? AXIS_TITLE_TO_TICK_LABEL_PADDING : 0) +
					COMMON_PADDING,
				marginTop: COMMON_PADDING * 2,
				marginRight: COMMON_PADDING * 2,
			};

	const groups = Array.from(new Set(data.map((d) => d._group))).map(
		(group) => ({
			group: group,
		})
	);

	const legendsAndValuesByGroup = lodashChain(data)
		.groupBy('_group')
		.mapValues((record) =>
			record.map((d) => ({
				legend: {
					name: d._barName,
					...legends[d._barName],
				},
				valueFormatted: yTickFormatBase(d._barValue, true),
			}))
		)
		.value();

	marks.push(
		hoverRectWithTooltip(groups, {
			isFacet: !isStackedBarChart,
			isHorizontal: isHorizontalBarChart,
			hoverOpacity: '0.03',
			tooltipRenderer: ({ x, y, groupIndex: dataIndex }) => {
				const group = groups[dataIndex];
				const legendAndValuePairs = legendsAndValuesByGroup[group.group];
				const groupValue = xTickFormat(group.group);
				return (
					<ChartTooltip x={x} y={y}>
						<TooltipContentRenderer
							groupValue={groupValue}
							legendAndValuePairs={legendAndValuePairs}
						/>
					</ChartTooltip>
				);
			},
		})
	);

	const plot = Plot.plot({
		marks,
		color: { range: colors, legend: false },
		width: chartWidth,
		...margins,
		height: chartHeight,
		y: {
			nice: true,
			grid: false, // Disable built-in grid (uses SVG currentColor), use custom grid marks instead
			percent: isHorizontalBarChart ? false : isPercentageStackedBarChart,
		},
		x: {
			grid: false, // Disable built-in grid (uses SVG currentColor), use custom grid marks instead
			nice: true,
			percent: isHorizontalBarChart ? isPercentageStackedBarChart : false,
		},
		fx: {
			padding: 0,
			nice: true,
		},
		fy: {
			padding: 0,
			nice: true,
		},
		style: {
			background: 'transparent',
			fontFamily: theme.fontFamily,
			fontSize: `${fontSize}px`,
		},
	});

	containerRef.current?.appendChild(plot);
	return {
		plot,
		legends,
	};
};
